// Convert to ES Module exports
// Define functions first, then export them all at the end

import authHeader from "services/auth-header";
import API from "services/axios";

const GetNameWithTimeStamp = (name) => {
  // Check if name is provided
  if (!name) {
    throw new Error("Name is required to generate a timestamped name.");
  }
  // Get current timestamp
  let timeStamp = new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "");

  // Split name into filename and extension
  const parts = name.split(".");
  const filename = parts.shift();
  const extension = parts.join(".");

  // Return Name with TimeStamp
  return `${filename}_${timeStamp}.${extension}`;
};

// Getting 12 Date Time from SQL Time
const convertSqlTimeTo12Hrs = (sqlTime) => {
  if (!sqlTime) {
    return;
  }
  let fullHours = sqlTime.split(":")[0];
  let minutes = sqlTime.split(":")[1];

  let shortHours = fullHours >= "12" ? fullHours - 12 : fullHours;
  let ampm = fullHours >= "12" ? "PM" : "AM";

  return `${shortHours}:${minutes} ${ampm}`;
};

const convertAttendanceRecordToTime = (record, showSeconds = true) => {
  let date = new Date(record);
  const options = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  };
  if (showSeconds) {
    options.second = "2-digit";
  }

  const localTime = date.toLocaleString("en-US", options);
  return localTime;
};

// Getting 12 Hours Time from SQL timestamp
const convertSqlTimestampToDate = (
  sqlTime,
  short = false,
  hideYear = false,
) => {
  // Without these, a missing or unparseable value reaches toLocaleString and
  // comes back as the literal string "Invalid Date" -- which has been printed
  // on customer-facing PDFs (the job board's W-Date, 2026-10-08). "---" is
  // what formatSqlDate already returns for a missing date, so the two agree.
  //
  // The falsy check is not redundant with the NaN one: new Date(null) is the
  // epoch, so a null column would otherwise print "January 1, 1970" -- a
  // worse failure than "Invalid Date", because it looks like real data.
  if (!sqlTime) {
    return "---";
  }

  const timestamp = sqlTime;
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "---";
  }

  let options = {
    month: short ? "numeric" : "long",
    day: "numeric",
  };

  if (!hideYear) {
    options.year = short ? "2-digit" : "numeric";
  }

  const time = date.toLocaleString("en-US", options);
  return time;
};

// Add a new function to rotate base64 images
const rotateBase64Image = (base64Image, callback) => {
  // Create a new image element
  const img = new Image();

  // Set up the onload handler
  img.onload = () => {
    // Create a canvas element
    const canvas = document.createElement("canvas");

    // Set canvas dimensions to accommodate rotated image
    // Swap width and height for 90-degree rotation
    canvas.width = img.height;
    canvas.height = img.width;

    const ctx = canvas.getContext("2d");

    // Clear canvas and apply transformations
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Move to the center of canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Rotate 90 degrees clockwise (Math.PI/2 radians)
    ctx.rotate(Math.PI / 2);

    // Draw the image, offset back
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    // Convert back to base64
    const rotatedBase64 = canvas.toDataURL("image/gif");

    // Remove the data URL prefix to get just the base64 string
    const rotatedBase64Data = rotatedBase64.replace(
      /^data:image\/(png|gif|jpeg);base64,/,
      "",
    );

    // Return the rotated image via callback
    callback(rotatedBase64Data);
  };

  // Set image source (must be after setting onload)
  img.src = `data:image/gif;base64,${base64Image}`;
};

const parseFloatData = (num, defaultDecimalPlaces = 3) => {
  // Gracefully handle null/undefined/empty values
  if (num === null || num === undefined) {
    return null;
  }

  let _num = num.toString().replace(/,/g, "");

  if (_num === "") {
    return null;
  }

  let [integerPart, decimalPart] = _num.split(".");
  if (decimalPart) {
    // Limit decimal part to specified digits
    decimalPart = decimalPart.slice(0, defaultDecimalPlaces);
    _num = `${integerPart}.${decimalPart}`;
  } else {
    // If no decimal part, retain only the integer part
    _num = integerPart;
  }

  return _num;
};

const truncateString = (str, length) => {
  if (!str || str.length <= length) {
    return str;
  }
  return str.slice(0, length) + "...";
};

const properCase = (text) => {
  if (!text || text.length === 0) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// const sql date-stamp to 24/04/25
const formatSqlDate = ({
  sqlDate,
  yearFormat = "2-digit",
  monthFormat = "2-digit",
  dayFormat = "2-digit",
  hideYear = false,
}) => {
  if (!sqlDate) return "---";

  // The New Date object is created.
  const date = new Date(sqlDate);

  // A truthy value that is not a date (a malformed string, an object) gets
  // past the check above and would print "Invalid Date". Same guard as
  // convertSqlTimestampToDate, same answer.
  if (Number.isNaN(date.getTime())) return "---";

  const options = {
    month: monthFormat,
    day: dayFormat,

    // CRITICAL: Forces the formatting to use UTC time, preventing day-shifting.
    timeZone: "UTC",
  };

  // Omitted entirely rather than set to undefined — Intl treats a missing key
  // and an undefined key the same, but leaving it out keeps the intent obvious.
  if (!hideYear) {
    options.year = yearFormat;
  }

  // CRITICAL: Forces the formatting to use U.S. English (en-US) style and words globally.
  return date.toLocaleDateString("en-US", options);
};

// Keep parsing until valid JSON is obtained
const parsedJsonString = (str) => {
  let parsed = str;
  let iterations = 0;
  const maxIterations = 5; // Prevent infinite loops
  while (typeof parsed === "string" && iterations < maxIterations) {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      break; // Exit loop if parsing fails
    }
    iterations++;
  }
  return parsed;
};

/**
 * Order a product's sizes the way the work order actually defines them.
 *
 * Each entry in `productSizes` carries its own `sortId`, but the stored array
 * is in insertion order -- add or reorder a size after the fact and the two
 * diverge. Rendering the raw array is why PDFs came out as "S, XS, M, L, XL".
 *
 * Accepts the raw JSON string or an already-parsed array.
 *
 * If any entry lacks a usable sortId the original order is returned untouched:
 * a partial key is worse than no key, and half-sorting would scramble the rows
 * we can't reason about while looking like it worked.
 */
const sortProductSizes = (productSizes) => {
  const sizes = parsedJsonString(productSizes);
  if (!Array.isArray(sizes)) return [];

  const usableSortId = (entry) =>
    entry &&
    entry.sortId !== undefined &&
    entry.sortId !== null &&
    entry.sortId !== "" &&
    Number.isFinite(Number(entry.sortId));

  if (!sizes.every(usableSortId)) return sizes;

  return [...sizes].sort((a, b) => Number(a.sortId) - Number(b.sortId));
};

/**
 * sortProductSizes, minus the sizes with no quantity (user, 2026-10-29): a
 * size set adds every size of the set, and the PDFs printed the ones left
 * empty as "S = ".
 */
const sizesWithQty = (productSizes) =>
  sortProductSizes(productSizes).filter((s) => parseInt(s?.quantity) > 0);

/**
 * Turn rich-text (Lexical / Quill) HTML into plain text.
 *
 * Stripping tags alone isn't enough: the editors emit entities, and `&nbsp;`
 * then renders as the literal six characters instead of a space. Anywhere
 * notes are shown outside a browser's HTML parser -- PDFs, the customer
 * approval page -- has to decode them explicitly.
 */
const stripHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    // Numeric entities, decimal and hex.
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16)));
};

// colors Array by id's
const getColorsArrayByIds = async (colorIds) => {
  if (!colorIds || colorIds.length === 0) {
    return;
  }

  try {
    const { data } = await API.post(
      "/sales-wo-colors-by-ids/products",
      { colorIds },
      { headers: authHeader() },
    );
    if (data?.success) {
      return data.data || [];
    } else {
      throw new Error(data?.message || "Failed to fetch colors by IDs.");
    }
  } catch (err) {
    throw err;
  }
};

// Export all functions with named exports (ES Modules style)
export {
  GetNameWithTimeStamp,
  convertSqlTimeTo12Hrs,
  convertAttendanceRecordToTime,
  parseFloatData,
  convertSqlTimestampToDate,
  rotateBase64Image,
  truncateString,
  properCase,
  formatSqlDate,
  parsedJsonString,
  sortProductSizes,
  sizesWithQty,
  stripHtml,
  getColorsArrayByIds,
};
